import { useCallback, useEffect, useState } from 'react';
import {
  applyTheme,
  nextMode,
  persistMode,
  readStoredMode,
  resolveTheme,
  systemPrefersDark,
  type ResolvedTheme,
  type ThemeMode,
} from './theme';

/** Shape returned by {@link useTheme}. */
export interface UseThemeResult {
  /** The user-selected mode (`light` | `dark` | `auto`). */
  mode: ThemeMode;
  /** The concrete theme currently rendered (`light` | `dark`). */
  resolved: ResolvedTheme;
  /** Set an explicit mode (persists the choice, or clears it for `auto`). */
  setMode: (mode: ThemeMode) => void;
  /** Advance to the next mode in the toggle cycle. */
  cycleMode: () => void;
}

/**
 * React hook owning the app's theme state.
 *
 * Responsibilities:
 * - Holds the current mode and keeps the resolved theme applied to <html>.
 * - Persists explicit overrides and clears them for `auto`.
 * - While in `auto`, subscribes to OS `prefers-color-scheme` changes so the UI
 *   updates live when the system theme flips. The subscription is torn down when
 *   the user picks an explicit mode (and re-established if they return to auto).
 *
 * Why a hook: the theme is genuinely stateful and shared by the header toggle and
 * the shell chrome, so a single source of truth avoids drift between them.
 */
export function useTheme(): UseThemeResult {
  const [mode, setModeState] = useState<ThemeMode>(() => readStoredMode());
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(readStoredMode()));

  // Keep <html> and the resolved value in sync whenever the mode changes.
  useEffect(() => {
    const next = resolveTheme(mode);
    setResolved(next);
    applyTheme(next);
  }, [mode]);

  // In `auto`, react to live OS theme changes.
  useEffect(() => {
    if (mode !== 'auto' || typeof window.matchMedia !== 'function') {
      return;
    }
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (): void => {
      const next: ResolvedTheme = systemPrefersDark() ? 'dark' : 'light';
      setResolved(next);
      applyTheme(next);
    };
    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, [mode]);

  const setMode = useCallback((next: ThemeMode) => {
    persistMode(next);
    setModeState(next);
  }, []);

  const cycleMode = useCallback(() => {
    setModeState((current) => {
      const next = nextMode(current);
      persistMode(next);
      return next;
    });
  }, []);

  return { mode, resolved, setMode, cycleMode };
}
